// A tray: a row of tabs and one panel for each tab. The compact layout makes
// one and puts the parts into the panels. The tray store picks
// the panel.

import { el } from '../lib/dom';
import { showTrayTab, type TrayTab, trayStore } from '../state/tray';

const TAB_LABELS: Record<TrayTab, string> = {
  spell: 'Spell',
  piece: 'Piece',
  log: 'Log',
  match: 'Match',
};

export type Tray<Tab extends TrayTab> = {
  root: HTMLElement;
  panels: Record<Tab, HTMLElement>;
};

const panelId = (tab: TrayTab): string => `tray-panel-${tab}`;

export const createTray = <Tab extends TrayTab>(tabs: readonly Tab[]): Tray<Tab> => {
  const tabButton = (tab: Tab): HTMLButtonElement => {
    const button = el(
      'button',
      {
        className: 'tray__tab',
        type: 'button',
        role: 'tab',
        dataset: { tip: TAB_LABELS[tab] },
        ariaLabel: TAB_LABELS[tab],
        onclick: () => showTrayTab(tab),
      },
      [el('span', { className: 'tray__label', textContent: TAB_LABELS[tab] })],
    );
    button.setAttribute('aria-controls', panelId(tab));
    return button;
  };

  const panel = (tab: Tab): HTMLElement =>
    el('div', {
      className: `tray__panel tray__panel--${tab}`,
      id: panelId(tab),
      role: 'tabpanel',
      ariaLabel: TAB_LABELS[tab],
    });

  const pairs = tabs.map((tab) => ({ tab, button: tabButton(tab), panel: panel(tab) }));
  const tabRow = el(
    'div',
    { className: 'tray__tabs', role: 'tablist' },
    pairs.map((pair) => pair.button),
  );
  const root = el('section', { className: 'tray' }, [tabRow, ...pairs.map((pair) => pair.panel)]);

  const draw = (shown: TrayTab): void => {
    for (const pair of pairs) {
      const isShown = pair.tab === shown;
      pair.button.classList.toggle('tray__tab--shown', isShown);
      pair.button.ariaSelected = String(isShown);
      pair.panel.hidden = !isShown;
    }
  };

  draw(trayStore.get());
  trayStore.subscribe(draw);

  const panels = Object.fromEntries(pairs.map((pair) => [pair.tab, pair.panel])) as Record<
    Tab,
    HTMLElement
  >;
  return { root, panels };
};
